import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnInit
} from '@angular/core';

import { DatePipe } from '@angular/common';
import { Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';

import { AuthService } from '../../auth/AuthService';
import { ReportService } from '../../Services/report.service';

type ExtractionStatus =
  | 'ALL'
  | 'SUCCESS'
  | 'FAILED';

type ClientScope =
  | 'SINGLE'
  | 'ALL';

interface ExtractionResponseItem {
  id: number;
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  memberId: string;
  clientId: number;
  officeId: number;
  source: string;
  status: string;
  createdAt: string;
}

interface ExtractionReportResponse {
  totalCount: number;
  successCount: number;
  failedCount: number;
  responses: ExtractionResponseItem[];
}


@Component({
  selector: 'pm-extractor-report',
  templateUrl: './extractor-report.component.html',
  styleUrls: ['./extractor-report.component.css']
})
export class ExtractorReportComponent implements OnInit {

  /* =========================
     FILTER DATA
     ========================= */

  clientScope: ClientScope = 'SINGLE';

  currentClientId: number | null = null;

  allAuthorizedClientIds: string[] = [];

  extractionStatuses: ExtractionStatus[] = [
    'ALL',
    'SUCCESS',
    'FAILED'
  ];

  selectedStatus: ExtractionStatus = 'ALL';
  Math = Math;

  extractionSources: string[] = [
    'CIGNA',
    'METLIFE',
    'DELTA_DENTAL_INS',
    'DELTA_DENTAL_INS_SHORTFORM',
    'DENTAL_TOOLKIT',
    'dentalxchange',
    'LIBERTY_DENTAL',
    'SCION',
    'MEDICAID',
    'DENTICON',
    'DHCS',
    'DENTAQUEST'
  ];

  selectedSources: string[] = [
    'CIGNA',
    'METLIFE',
    'DELTA_DENTAL_INS',
    'DELTA_DENTAL_INS_SHORTFORM',
    'DENTAL_TOOLKIT',
    'dentalxchange',
    'LIBERTY_DENTAL',
    'SCION',
    'MEDICAID',
    'DENTICON',
    'DHCS',
    'DENTAQUEST'
  ];

  startDate: Date = new Date();
  endDate: Date = new Date();
  todaysDate: Date = new Date();


  /* =========================
     RESULT DATA
     ========================= */

  totalCount = 0;
  successCount = 0;
  failedCount = 0;

  extractionRecords: ExtractionResponseItem[] = [];

  filteredRecords: ExtractionResponseItem[] = [];

  isLoading = false;
  loadError = false;


  /* =========================
     SEARCH
     ========================= */

  searchText = '';


  /* =========================
     CLIENT SIDE PAGINATION
     ========================= */

  currentPage = 1;
  pageSize = 10;

  pageSizeOptions: number[] = [
    10,
    25,
    50,
    100
  ];


  constructor(
    public authService: AuthService,
    private reportService: ReportService,
    private datePipe: DatePipe,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {
  }


  async ngOnInit(): Promise<void> {

    /*
     * Current dashboard/client selection.
     */
    this.currentClientId =
      this.authService.clientId ??
      this.authService.getClientId();

    /*
     * Decode ALL client IDs available to user.
     */
    await this.loadAuthorizedClientIds();

    /*
     * Initial report.
     */
    this.fetchExtractionReport();
  }


  /* =====================================================
     JWT CLIENT IDS
     ===================================================== */

  private async loadAuthorizedClientIds(): Promise<void> {

    try {

      const token =
        await this.authService.getAccessToken();

      if (!token) {
        this.allAuthorizedClientIds = [];
        return;
      }

      const decodedToken: any =
        jwtDecode(token);

      this.allAuthorizedClientIds =
        this.parseClientIds(
          decodedToken?.client_id
        );

    } catch (error) {

      console.error(
        'Unable to read client IDs from token:',
        error
      );

      this.allAuthorizedClientIds = [];
    }
  }


  private parseClientIds(
    clientIds: any
  ): string[] {

    if (!clientIds) {
      return [];
    }

    /*
     * If backend ever changes JWT to:
     *
     * client_id: [2,3,5]
     */
    if (Array.isArray(clientIds)) {

      return clientIds
        .map(id => String(id))
        .filter(Boolean);
    }


    /*
     * Current JWT format:
     *
     * client_id:
     * "[2, 3, 5, 6, 7, 9]"
     */
    if (typeof clientIds === 'string') {

      const cleaned =
        clientIds
          .replace('[', '')
          .replace(']', '')
          .trim();

      if (!cleaned) {
        return [];
      }

      return cleaned
        .split(',')
        .map(id => id.trim())
        .filter(Boolean);
    }


    /*
     * Single numeric client id.
     */
    return [
      String(clientIds)
    ];
  }


  /* =====================================================
     CLIENT SCOPE
     ===================================================== */

  setClientScope(
    scope: ClientScope
  ): void {

    if (this.clientScope === scope) {
      return;
    }

    this.clientScope = scope;

    this.currentPage = 1;

    this.fetchExtractionReport();
  }


  private getRequestedClientIds(): string[] {

    /*
     * ALL CLIENTS
     */
    if (this.clientScope === 'ALL') {

      return [
        ...this.allAuthorizedClientIds
      ];
    }


    /*
     * SINGLE CLIENT
     */
    const clientId =
      this.authService.clientId ??
      this.authService.getClientId();

    return clientId !== null &&
           clientId !== undefined
      ? [String(clientId)]
      : [];
  }


  /* =====================================================
     DATE
     ===================================================== */

  private formatDate(
    date: Date | string | null
  ): string | null {

    if (!date) {
      return null;
    }

    return this.datePipe.transform(
      new Date(date),
      'MM/dd/yyyy'
    );
  }


  /* =====================================================
     REQUEST
     ===================================================== */

  private buildRequest(): any {

    const request: any = {

      clientIds:
        this.getRequestedClientIds(),

      sources:
        [...this.selectedSources],

      fromDate:
        this.formatDate(
          this.startDate
        ),

      toDate:
        this.formatDate(
          this.endDate
        ),

      /*
       * This is the main difference
       * from Dashboard Extraction Metrics.
       */
      countsOnly: 'false'
    };


    /*
     * ALL = don't send status.
     */
    if (
      this.selectedStatus !== 'ALL'
    ) {

      request.status =
        this.selectedStatus;
    }


    return request;
  }


  /* =====================================================
     FETCH REPORT
     ===================================================== */

  fetchExtractionReport(): void {

    if (
      !this.selectedSources ||
      this.selectedSources.length === 0
    ) {
      return;
    }

    const request =
      this.buildRequest();

    this.ngZone.run(() => {

      this.isLoading = true;
      this.loadError = false;

      this.cdr.detectChanges();
    });


    this.reportService
      .getExtractionMetrics(request)
      .pipe(first())
      .subscribe({

        next: (
          response: ExtractionReportResponse
        ) => {

          this.ngZone.run(() => {

            this.totalCount =
              Number(
                response?.totalCount ?? 0
              );

            this.successCount =
              Number(
                response?.successCount ?? 0
              );

            this.failedCount =
              Number(
                response?.failedCount ?? 0
              );

            this.extractionRecords =
              response?.responses ?? [];

            this.applySearch();

            this.isLoading = false;

            this.cdr.detectChanges();
          });
        },


        error: (error) => {

          console.error(
            'Extraction report error:',
            error
          );

          this.ngZone.run(() => {

            this.totalCount = 0;
            this.successCount = 0;
            this.failedCount = 0;

            this.extractionRecords = [];
            this.filteredRecords = [];

            this.loadError = true;
            this.isLoading = false;

            this.cdr.detectChanges();
          });
        }
      });
  }


  /* =====================================================
     APPLY / RESET FILTER
     ===================================================== */

  applyFilters(): void {

    this.currentPage = 1;

    this.fetchExtractionReport();
  }


  resetFilters(): void {

    this.clientScope = 'SINGLE';

    this.selectedStatus = 'ALL';

    this.selectedSources = [
      ...this.extractionSources
    ];

    this.startDate =
      new Date();

    this.endDate =
      new Date();

    this.searchText = '';

    this.currentPage = 1;

    this.fetchExtractionReport();
  }


  /* =====================================================
     SEARCH
     ===================================================== */

  applySearch(): void {

    const search =
      (this.searchText || '')
        .trim()
        .toLowerCase();

    if (!search) {

      this.filteredRecords = [
        ...this.extractionRecords
      ];

      this.currentPage = 1;

      return;
    }


    this.filteredRecords =
      this.extractionRecords.filter(
        record => {

          const patientName =
            `${record.firstName ?? ''} ${record.lastName ?? ''}`
              .trim()
              .toLowerCase();

          const memberId =
            String(
              record.memberId ?? ''
            ).toLowerCase();

          const insurance =
            String(
              record.source ?? ''
            ).toLowerCase();

          return (
            patientName.includes(search) ||
            memberId.includes(search) ||
            insurance.includes(search)
          );
        }
      );

    this.currentPage = 1;
  }


  /* =====================================================
     PAGINATION
     ===================================================== */

  get paginatedRecords():
    ExtractionResponseItem[] {

    const start =
      (this.currentPage - 1) *
      this.pageSize;

    return this.filteredRecords.slice(
      start,
      start + this.pageSize
    );
  }


  get totalPages(): number {

    return Math.max(
      1,
      Math.ceil(
        this.filteredRecords.length /
        this.pageSize
      )
    );
  }


  previousPage(): void {

    if (this.currentPage > 1) {

      this.currentPage--;
    }
  }


  nextPage(): void {

    if (
      this.currentPage <
      this.totalPages
    ) {

      this.currentPage++;
    }
  }


  goToPage(
    page: number
  ): void {

    if (
      page >= 1 &&
      page <= this.totalPages
    ) {

      this.currentPage = page;
    }
  }


  onPageSizeChange(): void {

    this.currentPage = 1;
  }


  /* =====================================================
     HELPERS
     ===================================================== */

  get successPercentage(): number {

    if (!this.totalCount) {
      return 0;
    }

    return (
      this.successCount /
      this.totalCount
    ) * 100;
  }


  get failedPercentage(): number {

    if (!this.totalCount) {
      return 0;
    }

    return (
      this.failedCount /
      this.totalCount
    ) * 100;
  }


  getInsuranceDisplayName(
    source: string
  ): string {

    switch (source) {

      case 'DENTAL_TOOLKIT':
        return 'Dental Toolkit';

      case 'DELTA_DENTAL_INS':
        return 'Delta Dental INS';
       
      case 'DELTA_DENTAL_INS_SHORTFORM':
        return 'Delta Dental INS Shot Form';  

      case 'LIBERTY_DENTAL':
        return 'Liberty Dental';
      
      case 'DENTICON':
        return 'Denticon Pasting';  

      case 'dentalxchange':
        return 'Denticon Xchange';

      default:
        return source;
    }
  }

  getSelectedInsuranceLabel(): string {

  if (
    !this.selectedSources ||
    this.selectedSources.length === 0
  ) {
    return 'Select Insurance';
  }

  return this.selectedSources
    .map(source =>
      this.getInsuranceDisplayName(source)
    )
    .join(', ');
  }


  backToExtractor(): void {

    this.router.navigate([
      '/extractor'
    ]);
  }
}
